import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardLayout.css';

const DashboardLayout = ({
  displayName,
  pageTitle,
  dashboard,
  sections,
  onLogout,
  children,
}) => {
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth <= 920);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [openSectionKey, setOpenSectionKey] = useState(() => {
    const saved = localStorage.getItem('sidebar-open-section');
    if (saved) return saved;
    const firstDefaultOpen = (sections || []).find((s) => s.defaultOpen ?? true);
    return firstDefaultOpen?.key || (sections && sections[0]?.key) || '';
  });

  const userRef = useRef(null);
  const navigate = useNavigate();

  // ✅ FIX: sections undefined handle karo
  const visibleSections = useMemo(
    () =>
      (sections || []).map((section) => ({
        ...section,
        items: (section.items || []).filter((item) => !item.hidden),
      })),
    [sections]
  );

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem('sidebar-open-section', openSectionKey);
  }, [openSectionKey]);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 920;
      setIsMobileViewport(isMobile);
      if (!isMobile) {
        setIsMobileSidebarOpen(false);
      } else {
        setIsSidebarCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobileViewport) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = isMobileSidebarOpen ? 'hidden' : prev;
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileSidebarOpen, isMobileViewport]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        if (!isMobileViewport) {
          setIsSidebarCollapsed((prev) => !prev);
        }
      }
      if (e.key === 'Escape') {
        setIsMobileSidebarOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileViewport]);

  const handleSidebarToggle = useCallback(() => {
    if (isMobileViewport) {
      setIsMobileSidebarOpen(false);
      return;
    }
    setIsSidebarCollapsed((prev) => !prev);
  }, [isMobileViewport]);

  const closeMobileSidebarOnNavigate = useCallback((callback) => {
    if (typeof callback === 'function') {
      callback();
    }
    if (isMobileViewport) setIsMobileSidebarOpen(false);
  }, [isMobileViewport]);

  const handleSectionClick = useCallback((sectionKey) => {
    setOpenSectionKey((prev) => (prev === sectionKey ? '' : sectionKey));
  }, []);

  const pageClasses = [
    'dashboard-page',
    isSidebarCollapsed && !isMobileViewport ? 'sidebar-collapsed' : '',
    isMobileSidebarOpen ? 'mobile-sidebar-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <main className={pageClasses}>
      <aside className="dashboard-sidebar" role="navigation" aria-label="Main sidebar">
        <div className="sidebar-brand">
          <div className="brand-logo">
            <img
              src={process.env.PUBLIC_URL + '/images/logo.png'}
              alt="Company logo"
              className="brand-logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = document.createElement('div');
                placeholder.className = 'brand-logo-placeholder';
                placeholder.textContent = 'A';
                e.target.parentNode.insertBefore(placeholder, e.target);
              }}
            />
            <div className="brand-text">
              <span className="brand-name">Aryavihhar</span>
              <span className="brand-subtitle">Security Services</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav" aria-label="Sidebar navigation">
          <button
            type="button"
            className={`sidebar-link${dashboard?.active ? ' active' : ''}`}
            onClick={() => closeMobileSidebarOnNavigate(dashboard?.onClick || (() => {}))}
          >
            <span className="link-icon">🏠</span>
            <span className="link-text">Dashboard</span>
            {!isSidebarCollapsed && dashboard?.badge && (
              <span className="link-badge">{dashboard.badge}</span>
            )}
          </button>

          {/* ✅ SECTIONS - Candidate is here through sidebarSections */}
          {visibleSections.map((section) => {
            const isOpen = openSectionKey === section.key;
            const isActive = section.active || section.items.some((item) => item.active);

            return (
              <React.Fragment key={section.key}>
                <button
                  type="button"
                  className={`sidebar-link sidebar-module${isActive ? ' active' : ''}`}
                  onClick={() => handleSectionClick(section.key)}
                  aria-expanded={isOpen}
                  aria-controls={`${section.key}-submodules`}
                >
                  <span className="link-icon">{section.icon}</span>
                  <span className="link-text">{section.label}</span>
                  {!isSidebarCollapsed && section.badge && (
                    <span className="link-badge">{section.badge}</span>
                  )}
                  <span className={`module-caret${isOpen ? ' open' : ''}`}>▾</span>
                </button>

                <ul
                  id={`${section.key}-submodules`}
                  className={`sidebar-submodules${isOpen ? ' open' : ''}`}
                >
                  {section.items.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        className={`submodule-btn${item.active ? ' active' : ''}`}
                        onClick={() => closeMobileSidebarOnNavigate(item.onClick)}
                      >
                        <span className="submodule-dot">{item.icon}</span>
                        <span className="submodule-text">{item.label}</span>
                        {item.badge && <span className="submodule-badge">{item.badge}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </React.Fragment>
            );
          })}
        </nav>
      </aside>

      <button
        type="button"
        className="sidebar-mobile-backdrop"
        onClick={() => setIsMobileSidebarOpen(false)}
        aria-label="Close sidebar"
      />

      <section className="dashboard-main">
        <header className="dashboard-topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="sidebar-mobile-trigger"
              onClick={() => setIsMobileSidebarOpen((prev) => !prev)}
              aria-expanded={isMobileSidebarOpen}
              aria-label={isMobileSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            >
              ☰
            </button>
          </div>

          <div className="topbar-right">
            <div className="topbar-user" ref={userRef}>
              <div className="topbar-user-avatar">
                {displayName ? displayName.charAt(0).toUpperCase() : 'U'}
              </div>
              <span className="topbar-user-name">{displayName}</span>
              <span className="topbar-user-arrow">▾</span>
              <div className="topbar-user-dropdown">
                <button type="button" onClick={() => {}}>
                  👤 View Profile
                </button>
                <button type="button" onClick={() => {}}>
                  🔒 Account Settings
                </button>
                <div className="dropdown-divider" />
                <button
                  type="button"
                  onClick={onLogout}
                  className="logout-btn"
                >
                  🚪 Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <nav className="dashboard-breadcrumbs" aria-label="Breadcrumb">
          <ol>
            <li><button onClick={dashboard?.onClick}>🏠 Dashboard</button></li>
            {pageTitle && pageTitle !== 'Dashboard' && (
              <>
                <li className="separator">/</li>
                <li className="current">{pageTitle}</li>
              </>
            )}
          </ol>
        </nav>

        <header className="dashboard-header">
          <div className="header-left">
            <h2>{pageTitle}</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {children}
        </div>
      
      </section>
    </main>
  );
};

export default DashboardLayout;